import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = "G-P0DHMF3280";

/** Track a GA4 page view on every client-side route change. */
export function useAnalyticsPageView() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", { page_path: location.pathname + location.search });
  }, [location.pathname, location.search]);
}

/** Initialise gtag.js once at app startup (idempotent). */
export function initAnalytics() {
  if (typeof window === "undefined" || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
}

/** Send a GA4 event. Safe no-op when gtag has not loaded (yet). */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

/** Track a click on a call-to-action button. */
export function trackCtaClick(label: string, ctaLocation: string) {
  trackEvent("cta_click", {
    cta_label: label,
    cta_location: ctaLocation,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
  });
}



/** Track clicks on / results of a form submit button. */
export function trackFormSubmit(
  form: string,
  status: "attempt" | "success" | "error",
) {
  trackEvent("form_submit", {
    form_name: form,
    status,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
  });
}

/**
 * Track engagement on the current route: scroll depth milestones (25/50/75/100%)
 * and time spent on the page when the user leaves it.
 */
export function useEngagementTracking() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const start = Date.now();
    const path = location.pathname;
    const reached = new Set<number>();
    const milestones = [25, 50, 75, 100];

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable > 0 ? ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100 : 100;
      for (const m of milestones) {
        if (pct >= m && !reached.has(m)) {
          reached.add(m);
          trackEvent("scroll_depth", { percent: m, page_path: path });
        }
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const sendTime = () => {
      const seconds = Math.round((Date.now() - start) / 1000);
      if (seconds > 0) trackEvent("time_on_page", { seconds, page_path: path });
    };

    return () => {
      window.removeEventListener("scroll", onScroll);
      sendTime();
    };
  }, [location.pathname]);
}

/** Globally track clicks on phone, e-mail and WhatsApp links (once per app). */
export function useContactLinkTracking() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      let method: string | null = null;
      if (href.startsWith("tel:")) method = "phone";
      else if (href.startsWith("mailto:")) method = "email";
      else if (href.includes("wa.me") || href.includes("whatsapp")) method = "whatsapp";
      if (method) trackEvent("contact_click", { method, page_path: window.location.pathname });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}
